import React, { useState, useEffect } from 'react';
import {
  Dumbbell, Moon, Heart, Smile, Wine, Stethoscope,
  Users, Activity, Footprints, TrendingUp, TrendingDown,
  FlaskConical, Lock
} from 'lucide-react';
import { COLORS, SCORE_META } from '../data/mockData';
import { useAuth } from '../context/AuthContext';
import { fetchScores } from '../services/api';
import VideoWithSoundToggle from '../components/VideoWithSoundToggle';

const ICON_MAP = {
  Dumbbell, Moon, Heart, Smile, Wine, Stethoscope, Users, Activity, Footprints,
};

const PERIODS = [{ label: '7D', days: 7 }, { label: '30D', days: 30 }, { label: '90D', days: 90 }];

function ScoreRing({ score, size = 100 }) {
  const color = score >= 70 ? COLORS.success : score >= 45 ? COLORS.accentWarm : COLORS.error;
  const circumference = 2 * Math.PI * 42;
  const offset = circumference - (score / 100) * circumference;

  return (
    <div style={{ position: 'relative', width: size, height: size }}>
      <svg width={size} height={size} viewBox="0 0 100 100">
        <circle cx="50" cy="50" r="42" fill="none" stroke={`${color}20`} strokeWidth="8" />
        <circle cx="50" cy="50" r="42" fill="none" stroke={color} strokeWidth="8"
          strokeDasharray={circumference} strokeDashoffset={offset}
          strokeLinecap="round" transform="rotate(-90 50 50)"
          style={{ transition: 'stroke-dashoffset 1s ease' }} />
      </svg>
      <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ fontSize: size * 0.28, fontWeight: 800, color, fontFamily: 'Outfit, sans-serif' }}>{score}</span>
      </div>
    </div>
  );
}

function SparkLine({ values, color, width = 120, height = 24 }) {
  if (!values || values.length < 2) return null;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const barW = Math.max(width / values.length - 1, 3);
  return (
    <div style={{ width, height, display: 'flex', alignItems: 'flex-end', gap: 1 }}>
      {values.map((v, i) => {
        const barH = Math.max(((v - min) / range) * height, 2);
        const opacity = 0.3 + (i / values.length) * 0.7;
        return <div key={i} style={{ width: barW, height: barH, backgroundColor: color, opacity, borderRadius: 1 }} />;
      })}
    </div>
  );
}

function BarChartDisplay({ scores }) {
  const entries = Object.entries(SCORE_META).filter(([k]) => scores[k] !== undefined);
  return (
    <div style={{ backgroundColor: COLORS.surface, borderRadius: 16, padding: 20, marginBottom: 16 }}>
      <h3 style={{ fontSize: 16, fontWeight: 700, color: COLORS.textPrimary, marginBottom: 16, fontFamily: 'Outfit, sans-serif' }}>Metric Comparison</h3>
      <div style={{ display: 'flex', justifyContent: 'space-around', alignItems: 'flex-end', height: 200 }}>
        {entries.map(([key, meta]) => {
          const val = scores[key] || 0;
          const h = Math.max(val * 1.6, 8);
          const Icon = ICON_MAP[meta.icon];
          return (
            <div key={key} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
              <span style={{ fontSize: 10, fontWeight: 700, color: COLORS.textSecondary, marginBottom: 4 }}>{val}</span>
              <div style={{ width: 20, height: h, backgroundColor: meta.color, borderRadius: 4, transition: 'height 0.5s ease' }} />
              {Icon && <Icon size={13} color={meta.color} style={{ marginTop: 4 }} />}
              <span style={{ fontSize: 8, color: COLORS.textSecondary, marginTop: 2, fontWeight: 500 }}>{meta.label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function InsightsPage() {
  const { user } = useAuth();
  const [period, setPeriod] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const isEligible = (user?.membership_tier || 0) >= 2;

  useEffect(() => {
    if (isEligible) {
      fetchScores()
        .then(d => { setData(d); setLoading(false); })
        .catch(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [isEligible]);

  if (!isEligible) {
    return (
      <div style={{ maxWidth: 840, margin: '0 auto', padding: '24px' }}>
        <h1 style={{ fontSize: 26, fontWeight: 800, color: COLORS.textPrimary, fontFamily: 'Outfit, sans-serif' }}>Health Insights</h1>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '80px 32px' }}>
          <div style={{ width: 80, height: 80, borderRadius: 40, backgroundColor: '#E3F2FD', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 20 }}>
            <Lock size={48} color={COLORS.tierAnalyst} />
          </div>
          <h2 style={{ fontSize: 22, fontWeight: 800, color: COLORS.textPrimary, textAlign: 'center' }}>Tier 2+ Required</h2>
          <p style={{ fontSize: 15, color: COLORS.textSecondary, textAlign: 'center', marginTop: 10, lineHeight: 1.5 }}>Health scoring and visualization are available for Analyst tier and above.</p>
        </div>
      </div>
    );
  }

  if (loading) return <div style={{ padding: 24, textAlign: 'center', color: COLORS.textSecondary }}>Loading insights...</div>;

  const scores = data?.scores || {};
  const history = data?.history || [];
  const overall = data?.overall || 0;
  const estimatedLongevity = data?.estimated_longevity || 0;
  const yearsRemaining = data?.years_remaining || 0;

  const firstOverall = history.length > 1 ? history[0].overall : overall;
  const overallChange = overall - firstOverall;

  return (
    <div style={{ maxWidth: 840, margin: '0 auto', padding: '16px 24px 32px' }}>
      {/* INTRO VIDEO (members-only, 60% size) */}
      <div data-testid="insights-intro-video-wrap" style={{ marginBottom: 16 }}>
        <VideoWithSoundToggle
          src="/media/insights-intro.mp4"
          testId="insights-intro-video"
          aspectRatio="16 / 9"
          maxWidth={504}
          objectFit="cover"
        />
      </div>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 0', flexWrap: 'wrap', gap: 8 }}>
        <h1 style={{ fontSize: 26, fontWeight: 800, color: COLORS.textPrimary, fontFamily: 'Outfit, sans-serif', margin: 0 }}>Health Insights</h1>
        <div style={{ display: 'flex', gap: 4, backgroundColor: COLORS.surfaceSecondary, borderRadius: 8, padding: 3 }}>
          {PERIODS.map(p => (
            <button key={p.days} onClick={() => setPeriod(p.days)} style={{
              padding: '6px 14px', borderRadius: 6, border: 'none', cursor: 'pointer',
              backgroundColor: period === p.days ? COLORS.primary : 'transparent',
              color: period === p.days ? '#FFF' : COLORS.textSecondary,
              fontSize: 12, fontWeight: 600, transition: 'all 0.2s',
            }}>
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Overall Score Card */}
      <div style={{ backgroundColor: COLORS.surface, borderRadius: 24, padding: 24, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap' }}>
          <ScoreRing score={overall} />
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ fontSize: 12, color: COLORS.textSecondary, fontWeight: 500 }}>Estimated Longevity</div>
            <div style={{ fontSize: 28, fontWeight: 800, color: COLORS.textPrimary, marginBottom: 4, fontFamily: 'Outfit, sans-serif' }}>{estimatedLongevity} yrs</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
              {overallChange >= 0 ? <TrendingUp size={18} color={COLORS.success} /> : <TrendingDown size={18} color={COLORS.error} />}
              <span style={{ fontSize: 13, fontWeight: 700, color: overallChange >= 0 ? COLORS.success : COLORS.error }}>
                {overallChange >= 0 ? '+' : ''}{overallChange} pts over {period} days
              </span>
            </div>
            <div style={{ fontSize: 13, color: COLORS.textSecondary, fontWeight: 500 }}>{yearsRemaining} years remaining</div>
          </div>
        </div>
      </div>

      {/* Category Trends */}
      <h2 style={{ fontSize: 18, fontWeight: 700, color: COLORS.textPrimary, marginBottom: 12, marginTop: 8, fontFamily: 'Outfit, sans-serif' }}>Category Trends</h2>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
        {Object.entries(SCORE_META).map(([key, meta]) => {
          const Icon = ICON_MAP[meta.icon];
          const currentScore = scores[key] || 0;
          const values = history.map(h => h.scores?.[key] ?? 50);
          const firstVal = values.length > 1 ? values[0] : currentScore;
          const diff = currentScore - firstVal;
          return (
            <div key={key} style={{ flex: '1 1 calc(50% - 4px)', minWidth: 280, backgroundColor: COLORS.surface, borderRadius: 8, padding: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1 }}>
                <div style={{ width: 34, height: 34, borderRadius: 10, backgroundColor: `${meta.color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {Icon && <Icon size={18} color={meta.color} />}
                </div>
                <div>
                  <div style={{ fontSize: 12, color: COLORS.textSecondary, fontWeight: 500 }}>{meta.label}</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 20, fontWeight: 800, color: meta.color, fontFamily: 'Outfit, sans-serif' }}>{currentScore}</span>
                    {diff !== 0 && (
                      <span style={{ display: 'flex', alignItems: 'center', gap: 2, fontSize: 11, fontWeight: 700, color: diff > 0 ? COLORS.success : COLORS.error }}>
                        {diff > 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                        {diff > 0 ? '+' : ''}{diff}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <SparkLine values={values} color={meta.color} />
            </div>
          );
        })}
      </div>

      {/* Bar Chart */}
      <h2 style={{ fontSize: 18, fontWeight: 700, color: COLORS.textPrimary, marginBottom: 12, marginTop: 8, fontFamily: 'Outfit, sans-serif' }}>Metric Overview</h2>
      <BarChartDisplay scores={scores} />

      {/* What-If */}
      <div style={{ marginBottom: 24 }}>
        <button style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '14px 20px', borderRadius: 9999, border: `1.5px solid ${COLORS.primary}`, backgroundColor: 'transparent', cursor: 'pointer', transition: 'all 0.2s' }}>
          <FlaskConical size={20} color={COLORS.primary} />
          <span style={{ fontSize: 15, fontWeight: 600, color: COLORS.primary }}>What-If Analysis</span>
        </button>
      </div>
    </div>
  );
}
